import type { DashboardSummary, Item, PublicUser } from "@gift-registry/shared";
import type { SignInValues, SignUpValues } from "./forms.js";
import { encodePasswordForSend } from "./password-crypto.js";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.trim() || "/api";

export interface ApiErrorPayload {
  error: string;
  fields: Record<string, string> | undefined;
}

export class ApiError extends Error {
  status: number;
  fields: Record<string, string> | undefined;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.error);
    this.status = status;
    this.fields = payload.fields;
  }
}

export async function signUp(values: SignUpValues): Promise<{ user: PublicUser }> {
  const password = await encodePasswordForSend(values.password);
  const body = new FormData();
  body.set("firstName", values.firstName);
  body.set("lastName", values.lastName);
  body.set("email", values.email);
  body.set("password", password);

  return requestJson<{ user: PublicUser }>("/api/v1/auth/register", {
    method: "POST",
    body,
  });
}

export async function signIn(values: SignInValues): Promise<{ user: PublicUser }> {
  const password = await encodePasswordForSend(values.password);
  const body = new FormData();
  body.set("email", values.email);
  body.set("password", password);

  return requestJson<{ user: PublicUser }>("/api/v1/auth/login", {
    method: "POST",
    body,
  });
}

export async function signOut(): Promise<void> {
  await requestJson<{ ok: true }>("/api/v1/auth/logout", {
    method: "POST",
  });
}

export async function loadSession(): Promise<{ user: PublicUser }> {
  return requestJson<{ user: PublicUser }>("/api/v1/auth/me");
}

export async function loadDashboard(): Promise<{
  user: PublicUser;
  dashboard: DashboardSummary;
}> {
  return requestJson<{ user: PublicUser; dashboard: DashboardSummary }>(
    "/api/v1/dashboard"
  );
}

export async function createWishlist(input: {
  name: string;
  description?: string | null;
  visibility?: "private" | "public" | "protected" | "limited";
}): Promise<{ wishlist: DashboardSummary["ownedLists"][number] }> {
  return requestJson<{ wishlist: DashboardSummary["ownedLists"][number] }>(
    "/api/v1/dashboard/wishlists",
    { method: "POST", body: JSON.stringify(input) }
  );
}

export async function updateWishlist(
  wishlistId: string,
  input: { name?: string; description?: string | null; visibility?: "private" | "public" | "protected" | "limited"; version: number }
): Promise<{ wishlist: DashboardSummary["ownedLists"][number] }> {
  return requestJson<{ wishlist: DashboardSummary["ownedLists"][number] }>(
    `/api/v1/dashboard/wishlists/${wishlistId}`,
    { method: "PUT", body: JSON.stringify(input) }
  );
}

export async function deleteWishlist(wishlistId: string): Promise<{ ok: true }> {
  return requestJson<{ ok: true }>(`/api/v1/dashboard/wishlists/${wishlistId}`, {
    method: "DELETE",
  });
}

export async function cloneWishlist(wishlistId: string): Promise<{ wishlist: DashboardSummary["ownedLists"][number] }> {
  return requestJson<{ wishlist: DashboardSummary["ownedLists"][number] }>(
    `/api/v1/dashboard/wishlists/${wishlistId}/clone`,
    { method: "POST" }
  );
}

export async function setWishlistPasscode(
  wishlistId: string,
  input: { code: string; expiresAt?: string | null }
): Promise<{ passcode: { id: string; wishlistId: string; expiresAt: string | null } }> {
  return requestJson<{ passcode: { id: string; wishlistId: string; expiresAt: string | null } }>(
    `/api/v1/dashboard/wishlists/${wishlistId}/passcodes`,
    { method: "POST", body: JSON.stringify(input) }
  );
}

export async function listWishlistShareLinks(wishlistId: string): Promise<{
  links: Array<{
    id: string;
    wishlistId: string;
    createdByUserId: string;
    token: string;
    label: string | null;
    expiresAt: string | null;
    revokedAt: string | null;
    createdAt: string;
  }>;
}> {
  return requestJson<{ links: Array<{ id: string; wishlistId: string; createdByUserId: string; token: string; label: string | null; expiresAt: string | null; revokedAt: string | null; createdAt: string }> }>(
    `/api/v1/dashboard/wishlists/${wishlistId}/share-links`
  );
}

export async function createWishlistShareLink(
  wishlistId: string,
  input: { label?: string | null; expiresAt?: string | null }
): Promise<{ link: { id: string; wishlistId: string; createdByUserId: string; token: string; label: string | null; expiresAt: string | null; revokedAt: string | null; createdAt: string } }> {
  return requestJson<{ link: { id: string; wishlistId: string; createdByUserId: string; token: string; label: string | null; expiresAt: string | null; revokedAt: string | null; createdAt: string } }>(
    `/api/v1/dashboard/wishlists/${wishlistId}/share-links`,
    { method: "POST", body: JSON.stringify(input) }
  );
}

export async function revokeWishlistShareLink(wishlistId: string, linkId: string): Promise<{ ok: true }> {
  return requestJson<{ ok: true }>(`/api/v1/dashboard/wishlists/${wishlistId}/share-links/${linkId}`, {
    method: "DELETE",
  });
}

export async function addWishlistMember(
  wishlistId: string,
  input: { userId: string; role: "collaborator" | "viewer" }
): Promise<{ member: { id: string; wishlistId: string; userId: string; role: "collaborator" | "viewer"; createdAt: string } }> {
  return requestJson<{ member: { id: string; wishlistId: string; userId: string; role: "collaborator" | "viewer"; createdAt: string } }>(
    `/api/v1/dashboard/wishlists/${wishlistId}/members`,
    { method: "POST", body: JSON.stringify(input) }
  );
}

export async function listWishlistItems(wishlistId: string): Promise<{ items: Item[] }> {
  return requestJson<{ items: Item[] }>(`/api/v1/dashboard/wishlists/${wishlistId}/items`);
}

export async function createWishlistItem(
  wishlistId: string,
  input: {
    name: string;
    link: string;
    price?: number | null;
    quantity?: number | null;
    hearts?: 0 | 1 | 2 | 3;
    tags?: string[];
    options?: Array<{ key: string; value: string }>;
  }
): Promise<{ item: Item }> {
  return requestJson<{ item: Item }>(`/api/v1/dashboard/wishlists/${wishlistId}/items`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateWishlistItem(
  wishlistId: string,
  itemId: string,
  input: {
    name?: string;
    link?: string;
    price?: number | null;
    quantity?: number | null;
    hearts?: 0 | 1 | 2 | 3;
    tags?: string[];
    options?: Array<{ key: string; value: string }>;
    version: number;
  }
): Promise<{ item: Item }> {
  return requestJson<{ item: Item }>(`/api/v1/dashboard/wishlists/${wishlistId}/items/${itemId}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function deleteWishlistItem(wishlistId: string, itemId: string): Promise<{ ok: true }> {
  return requestJson<{ ok: true }>(`/api/v1/dashboard/wishlists/${wishlistId}/items/${itemId}`, {
    method: "DELETE",
  });
}

export async function importWishlistItemMetadata(
  wishlistId: string,
  input: { link: string }
): Promise<{ result: { supported: boolean; metadata: { name: string; price: number | null } | null; link: string } }> {
  return requestJson<{ result: { supported: boolean; metadata: { name: string; price: number | null } | null; link: string } }>(
    `/api/v1/dashboard/wishlists/${wishlistId}/items/import`,
    { method: "POST", body: JSON.stringify(input) }
  );
}

async function requestJson<T>(
  input: string,
  init: RequestInit = {}
): Promise<T> {
  const response = await fetch(resolveApiUrl(input), {
    ...init,
    credentials: "include",
    headers: buildHeaders(init.headers, init.body),
  });

  if (!response.ok) {
    throw new ApiError(response.status, await readErrorPayload(response));
  }

  return (await response.json()) as T;
}

async function readErrorPayload(response: Response): Promise<ApiErrorPayload> {
  try {
    const payload = (await response.json()) as Partial<ApiErrorPayload>;
    return {
      error: typeof payload.error === "string" ? payload.error : "Request failed",
      fields: payload.fields,
    };
  } catch {
    return { error: "Request failed", fields: undefined };
  }
}

export function resolveApiUrl(input: string): string {
  if (/^https?:\/\//i.test(API_BASE_URL)) {
    return new URL(input, API_BASE_URL).toString();
  }

  const basePath = API_BASE_URL.startsWith("/") ? API_BASE_URL : `/${API_BASE_URL}`;
  if (input === basePath || input.startsWith(`${basePath}/`)) {
    return input;
  }

  return `${basePath}${input.startsWith("/") ? input : `/${input}`}`;
}

function buildHeaders(headers: HeadersInit | undefined, body: BodyInit | null | undefined): Headers {
  const nextHeaders = new Headers(headers ?? {});
  if (typeof body === "string" && !nextHeaders.has("Content-Type")) {
    nextHeaders.set("Content-Type", "application/json");
  }
  return nextHeaders;
}